import React, { useState, useEffect } from "react";
import Modal from "../../../components/common/Modal";
import Toggle from "../../../components/common/Toggle";
import Input from "../../../components/common/Input";
import Button from "../../../components/common/Button";
import { ClockIcon } from "@heroicons/react/outline";

const DevicePrefsModal = ({ isOpen, onClose, device, onSave }) => {
  const [prefs, setPrefs] = useState({
    promoPush: true,
    promoWhatsApp: true,
  });
  const [quietHours, setQuietHours] = useState({
    enabled: false,
    start: 21,
    end: 8,
  });

  useEffect(() => {
    if (device) {
      setPrefs(device.prefs || { promoPush: true, promoWhatsApp: true });
      if (device.quietHours && device.quietHours.start !== undefined) {
        setQuietHours({
          enabled: true,
          start: device.quietHours.start,
          end: device.quietHours.end,
        });
      } else {
        setQuietHours({ enabled: false, start: 21, end: 8 });
      }
    }
  }, [device]);

  const handleSave = () => {
    const updates = {
      prefs,
      quietHours: quietHours.enabled
        ? { start: quietHours.start, end: quietHours.end }
        : null,
    };
    onSave(device?._id, updates);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      title="Edit Device Preferences"
      size="md"
    >
      <div className="space-y-6">
        {/* Notification Preferences */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Notification Preferences
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <label className="text-sm text-gray-600 dark:text-gray-400">
                Promotional Push Notifications
              </label>
              <Toggle
                enabled={prefs.promoPush}
                onChange={(enabled) =>
                  setPrefs({ ...prefs, promoPush: enabled })
                }
              />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <label className="text-sm text-gray-600 dark:text-gray-400">
                Promotional WhatsApp Messages
              </label>
              <Toggle
                enabled={prefs.promoWhatsApp}
                onChange={(enabled) =>
                  setPrefs({ ...prefs, promoWhatsApp: enabled })
                }
              />
            </div>
          </div>
        </div>

        {/* Quiet Hours */}
        <div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Quiet Hours
            </h3>
            <Toggle
              enabled={quietHours.enabled}
              onChange={(enabled) => setQuietHours({ ...quietHours, enabled })}
            />
          </div>

          {quietHours.enabled && (
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-3">
                <ClockIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  No notifications during these hours
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Start Time
                  </label>
                  <select
                    value={quietHours.start}
                    onChange={(e) =>
                      setQuietHours({
                        ...quietHours,
                        start: parseInt(e.target.value),
                      })
                    }
                    className="w-full px-2 sm:px-3 py-1 sm:py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
                  >
                    {[...Array(24)].map((_, i) => (
                      <option key={i} value={i}>
                        {i}:00
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    End Time
                  </label>
                  <select
                    value={quietHours.end}
                    onChange={(e) =>
                      setQuietHours({
                        ...quietHours,
                        end: parseInt(e.target.value),
                      })
                    }
                    className="w-full px-2 sm:px-3 py-1 sm:py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
                  >
                    {[...Array(24)].map((_, i) => (
                      <option key={i} value={i}>
                        {i}:00
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <Button
          variant="ghost"
          onClick={onClose}
          size="sm"
          className="px-2 sm:px-4"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          size="sm"
          className="px-2 sm:px-4"
        >
          Save Changes
        </Button>
      </div>
    </Modal>
  );
};

export default DevicePrefsModal;
